import React, { useCallback, useMemo, useState } from 'react';
import {
  Autocomplete,
  BlockStack,
  Button,
  Card,
  Checkbox,
  InlineError,
  InlineStack,
  Select,
  Tag,
  TextField,
  Text,
  Thumbnail
} from '@shopify/polaris';
import {
  ADDONS,
  GIFT_MESSAGE_MAX,
  LANGUAGES,
  LIMITS
} from '../../../constants/storefrontOrderConstants';
import { validateEntry, validateKidName } from '../../../utils/storefrontOrder/validation';
import PhotoUploader from './PhotoUploader';

const isLegacyStoryType = (story) => story?.story_type === 'legacy_static';

/**
 * Premium printing for the new selection. Legacy stories are premium only, so
 * premium turns on whenever premium data exists. Dynamic stories keep the
 * customer's choice and only stay ticked while the selection supports it.
 */
const nextPremiumPrinting = ({ story, variant, variants, previous }) => {
  const premiumAvailable = variant
    ? Boolean(variant.premium)
    : variants.some(item => item.premium);
  if (isLegacyStoryType(story)) {
    return premiumAvailable;
  }
  return Boolean(previous) && premiumAvailable;
};

/**
 * One story line of the dashboard order form, mirroring the Shopify
 * storefront form: story picker, size, language, kid name, and photos.
 *
 * @param {Object} props
 * @param {Object} props.entry - { key, story, variant, languageCode, kidName, photos }.
 * @param {number} props.index - Position in the entries list (for the title).
 * @param {Object[]} props.catalog - Story catalog entries.
 * @param {Function} props.onChange - Receives the full next entry.
 * @param {Function} [props.onRemove]
 * @param {boolean} [props.canRemove]
 * @param {boolean} [props.showErrors]
 * @returns {JSX.Element}
 */
export default function StoryEntryCard({
  entry,
  index,
  catalog,
  onChange,
  onRemove,
  canRemove = false,
  showErrors = false
}) {
  const [query, setQuery] = useState('');
  const { story } = entry;

  const availableVariants = useMemo(
    () => (story ? (story.variants || []).filter(variant => variant.available !== false) : []),
    [story]
  );

  // Premium printing is only offered when the current selection supports it:
  // the chosen variant carries premium data, or (no variant chosen yet) any
  // selectable variant of the story does (spec addendum 8.2).
  const premiumSupported = useMemo(() => {
    if (entry.variant) {
      return Boolean(entry.variant.premium);
    }
    return availableVariants.some(variant => variant.premium);
  }, [entry.variant, availableVariants]);

  const storyOptions = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return catalog
      .filter(item => {
        if (!needle) {
          return true;
        }
        const haystack = [
          item.title,
          item.handle,
          ...(item.variants || []).map(variant => variant.sku)
        ]
          .join(' ')
          .toLowerCase();
        return haystack.includes(needle);
      })
      .map(item => ({
        value: item.id,
        label: item.title,
        ...(item.image
          ? { media: <Thumbnail source={item.image} alt={item.title} size="small" /> }
          : {})
      }));
  }, [catalog, query]);

  const errors = useMemo(
    () => (showErrors ? validateEntry(entry) : []),
    [entry, showErrors]
  );
  const kidNameError = showErrors ? validateKidName(entry.kidName, entry.languageCode) : null;
  // The kid name error is already shown on its field; avoid repeating it.
  const otherErrors = errors.filter(message => message !== kidNameError);

  const handleSelectStory = useCallback(
    ([storyId]) => {
      const nextStory = catalog.find(item => item.id === storyId);
      if (!nextStory) {
        return;
      }
      const nextVariants = (nextStory.variants || []).filter(
        variant => variant.available !== false
      );
      const languageCode = nextStory.languages.includes('en')
        ? 'en'
        : nextStory.languages[0];
      const nextVariant = nextVariants.length === 1 ? nextVariants[0] : null;
      onChange({
        ...entry,
        story: nextStory,
        variant: nextVariant,
        languageCode,
        premiumPrinting: nextPremiumPrinting({
          story: nextStory,
          variant: nextVariant,
          variants: nextVariants,
          previous: entry.premiumPrinting
        })
      });
      setQuery('');
    },
    [catalog, entry, onChange]
  );

  const handleSelectVariant = useCallback(
    variantId => {
      const variant = availableVariants.find(item => String(item.id) === String(variantId));
      if (variant) {
        onChange({
          ...entry,
          variant,
          premiumPrinting: nextPremiumPrinting({
            story,
            variant,
            variants: availableVariants,
            previous: entry.premiumPrinting
          })
        });
      }
    },
    [availableVariants, entry, onChange, story]
  );

  const handleSelectLanguage = useCallback(
    code => {
      onChange({ ...entry, languageCode: code });
    },
    [entry, onChange]
  );

  const handleKidNameChange = useCallback(
    kidName => {
      onChange({ ...entry, kidName });
    },
    [entry, onChange]
  );

  const handlePhotosChange = useCallback(
    photos => {
      onChange({ ...entry, photos });
    },
    [entry, onChange]
  );

  const handleQueryChange = useCallback(nextQuery => {
    setQuery(nextQuery);
  }, []);

  const handleAddonToggle = useCallback(
    (field, checked) => {
      // Unchecking the gift note clears its message so stale text cannot
      // reappear when it is re-enabled.
      const next = { ...entry, [field]: checked };
      if (field === 'giftNote' && !checked) {
        next.giftMessage = '';
      }
      onChange(next);
    },
    [entry, onChange]
  );

  const handleGiftMessageChange = useCallback(
    giftMessage => {
      onChange({ ...entry, giftMessage });
    },
    [entry, onChange]
  );

  const variantSelectMarkup =
    story && availableVariants.length > 1 ? (
      <Select
        label="Size"
        options={availableVariants.map(variant => ({
          value: String(variant.id),
          label: variant.title
        }))}
        placeholder="Select a size"
        value={entry.variant ? String(entry.variant.id) : ''}
        onChange={handleSelectVariant}
      />
    ) : null;

  const languageButtonsMarkup = story ? (
    <InlineStack gap="200">
      {LANGUAGES.filter(language => story.languages.includes(language.code)).map(
        language => (
          <Button
            key={language.code}
            pressed={entry.languageCode === language.code}
            onClick={() => handleSelectLanguage(language.code)}
          >
            {language.label}
          </Button>
        )
      )}
    </InlineStack>
  ) : null;

  const addonsMarkup = story ? (
    <BlockStack gap="200">
      <Text variant="headingSm" as="h4">
        Add-ons
      </Text>
      <Checkbox
        label={ADDONS.giftBox.label}
        checked={Boolean(entry.giftBox)}
        onChange={checked => handleAddonToggle('giftBox', checked)}
      />
      <Checkbox
        label={ADDONS.giftNote.label}
        checked={Boolean(entry.giftNote)}
        onChange={checked => handleAddonToggle('giftNote', checked)}
      />
      {entry.giftNote ? (
        <TextField
          label="Gift message"
          value={entry.giftMessage || ''}
          onChange={handleGiftMessageChange}
          multiline={3}
          maxLength={GIFT_MESSAGE_MAX}
          showCharacterRemaining
          autoComplete="off"
        />
      ) : null}
      {isLegacyStoryType(story) ? (
        <Text as="p" tone="subdued">
          Premium printing (this story is premium only)
        </Text>
      ) : null}
      {premiumSupported && !isLegacyStoryType(story) ? (
        <Checkbox
          label="Premium printing"
          checked={Boolean(entry.premiumPrinting)}
          onChange={checked => handleAddonToggle('premiumPrinting', checked)}
        />
      ) : null}
    </BlockStack>
  ) : null;

  return (
    <Card>
      <BlockStack gap="400">
        <InlineStack gap="300" blockAlign="center">
          <Text variant="headingSm" as="h3">
            Story {index + 1}
          </Text>
          {story ? <Tag>{story.title}</Tag> : null}
          {story && story.image ? (
            <Thumbnail source={story.image} alt={story.title} size="small" />
          ) : null}
          {canRemove && onRemove ? (
            <InlineStack gap="200" align="end">
              <Button plain destructive onClick={onRemove}>
                Remove
              </Button>
            </InlineStack>
          ) : null}
        </InlineStack>

        <Autocomplete
          options={storyOptions}
          selected={story ? [story.id] : []}
          onSelect={handleSelectStory}
          textField={
            <Autocomplete.TextField
              label="Story"
              value={query}
              placeholder={story ? story.title : 'Search stories'}
              onChange={handleQueryChange}
              autoComplete="off"
            />
          }
        />

        {variantSelectMarkup}

        {languageButtonsMarkup}

        {addonsMarkup}

        <TextField
          label="Kid's name"
          value={entry.kidName || ''}
          onChange={handleKidNameChange}
          maxLength={LIMITS.KID_NAME_MAX}
          error={kidNameError}
          showCharacterRemaining
        />

        <PhotoUploader
          photos={entry.photos || []}
          onChange={handlePhotosChange}
          disabled={false}
        />

        {otherErrors.length > 0 ? (
          <InlineError fieldID={`entry-error-${entry.key}`} message={otherErrors[0]} />
        ) : null}
      </BlockStack>
    </Card>
  );
}
